import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { Droplet, Milk, Activity, TrendingUp, Syringe, StickyNote } from "lucide-react";

const TABS = [
  { key: "diaper", label: "Wet Diapers", icon: Droplet },
  { key: "stool", label: "Stool", icon: Activity },
  { key: "feeding", label: "Feeding", icon: Milk },
  { key: "growth", label: "Growth", icon: TrendingUp },
  { key: "vaccine", label: "Vaccines", icon: Syringe },
  { key: "symptom", label: "Symptom Diary", icon: StickyNote },
];

const VAX = [
  {age:"Birth", vax:"BCG, OPV-0, Hep-B 1"},
  {age:"6 weeks", vax:"DTwP/DTaP 1, IPV 1, Hib 1, Hep-B 2, Rota 1, PCV 1"},
  {age:"10 weeks", vax:"DTwP/DTaP 2, IPV 2, Hib 2, Rota 2, PCV 2"},
  {age:"14 weeks", vax:"DTwP/DTaP 3, IPV 3, Hib 3, Rota 3, PCV 3"},
  {age:"6 months", vax:"Influenza 1"},
  {age:"9 months", vax:"MMR 1, Typhoid Conjugate"},
  {age:"12 months", vax:"Hep-A 1"},
  {age:"15 months", vax:"MMR 2, Varicella 1, PCV Booster"},
  {age:"18 months", vax:"DTwP/DTaP B1, IPV B1, Hib B1, Hep-A 2"},
];

export default function Trackers() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("diaper");
  const [children, setChildren] = useState([]);
  const [childId, setChildId] = useState("");
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState({});

  useEffect(() => {
    if (!user) { nav("/login"); return; }
    api.get("/children").then((r) => {
      setChildren(r.data);
      if (r.data.length) setChildId(r.data[0].id);
    });
  }, [user, nav]);

  useEffect(() => {
    if (!childId) return;
    api.get(`/trackers/${childId}`, { params: { kind: tab } }).then((r) => setEntries(r.data));
  }, [childId, tab]);

  const submit = async (e) => {
    e.preventDefault();
    if (!childId) return alert("Add a child first");
    const { data } = await api.post("/trackers", { child_id: childId, kind: tab, data: form });
    setEntries([data, ...entries]);
    setForm({});
  };

  if (!user) return null;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="trackers-page">
      <div className="overline mb-1">Growth & Feeding Tools</div>
      <h1 className="font-display font-black text-3xl sm:text-4xl mb-2" style={{color:"#232F2F"}}>Track baby's daily health</h1>
      <p className="text-[#5A6D6D] mb-6 text-sm">Educational tracking only — not for diagnosis. Book a consultation for any concerns.</p>

      <div className="card-soft mb-4">
        <label className="text-xs font-semibold text-[#5A6D6D]">SELECT CHILD</label>
        <select value={childId} onChange={(e)=>setChildId(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-xl border border-[#E5EBEB]" data-testid="tracker-child-select">
          {children.length === 0 && <option>Please add a child in the booking flow first</option>}
          {children.map((c) => <option key={c.id} value={c.id}>{c.name} · DOB {c.dob}</option>)}
        </select>
      </div>

      <div className="flex gap-2 overflow-x-auto mb-4">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.key} onClick={()=>{ setTab(t.key); setForm({}); }} className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 ${tab===t.key ? 'bg-[#2D7A78] text-white' : 'bg-white border border-[#E5EBEB] text-[#5A6D6D]'}`} data-testid={`tracker-tab-${t.key}`}>
              <Icon size={16}/>{t.label}
            </button>
          );
        })}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <form onSubmit={submit} className="card-soft space-y-3">
          <h3 className="font-display font-bold">Add entry</h3>
          {tab === "diaper" && (
            <>
              <input type="date" required className="w-full px-3 py-2 rounded-xl border border-[#E5EBEB]" onChange={(e)=>setForm({...form, date: e.target.value})} data-testid="diaper-date"/>
              <input type="number" required placeholder="Wet diaper count" className="w-full px-3 py-2 rounded-xl border border-[#E5EBEB]" onChange={(e)=>setForm({...form, count: Number(e.target.value)})} data-testid="diaper-count"/>
              {form.count && form.count < 4 && <div className="text-xs text-[#B3261E] bg-[#FFE2E2] p-2 rounded">Low wet diaper count — could indicate dehydration. Consult doctor.</div>}
            </>
          )}
          {tab === "stool" && (
            <>
              <input type="date" required className="w-full px-3 py-2 rounded-xl border border-[#E5EBEB]" onChange={(e)=>setForm({...form, date: e.target.value})}/>
              <input placeholder="Frequency (e.g. 3 times)" className="w-full px-3 py-2 rounded-xl border border-[#E5EBEB]" onChange={(e)=>setForm({...form, frequency: e.target.value})}/>
              <select className="w-full px-3 py-2 rounded-xl border border-[#E5EBEB]" onChange={(e)=>setForm({...form, colour: e.target.value})}>
                <option value="">Colour</option><option>Yellow</option><option>Green</option><option>Brown</option><option>Black</option><option>Red</option><option>White/pale</option>
              </select>
              <select className="w-full px-3 py-2 rounded-xl border border-[#E5EBEB]" onChange={(e)=>setForm({...form, consistency: e.target.value})}>
                <option value="">Consistency</option><option>Normal</option><option>Loose/Watery</option><option>Hard</option><option>Mucus</option>
              </select>
              {(form.colour === "Red" || form.colour === "Black" || form.consistency === "Mucus") && <div className="text-xs text-[#B3261E] bg-[#FFE2E2] p-2 rounded">Warning: This may indicate a serious problem. Please consult immediately.</div>}
            </>
          )}
          {tab === "feeding" && (
            <>
              <input type="datetime-local" required className="w-full px-3 py-2 rounded-xl border border-[#E5EBEB]" onChange={(e)=>setForm({...form, time: e.target.value})}/>
              <select className="w-full px-3 py-2 rounded-xl border border-[#E5EBEB]" onChange={(e)=>setForm({...form, type: e.target.value})}>
                <option value="">Type</option><option>Breastfeeding</option><option>Formula</option><option>Mixed</option><option>Complementary</option>
              </select>
              <input placeholder="Duration or amount (e.g. 15 min / 60ml)" className="w-full px-3 py-2 rounded-xl border border-[#E5EBEB]" onChange={(e)=>setForm({...form, amount: e.target.value})}/>
            </>
          )}
          {tab === "growth" && (
            <>
              <input type="date" required className="w-full px-3 py-2 rounded-xl border border-[#E5EBEB]" onChange={(e)=>setForm({...form, date: e.target.value})}/>
              <input type="number" step="0.01" placeholder="Weight (kg)" className="w-full px-3 py-2 rounded-xl border border-[#E5EBEB]" onChange={(e)=>setForm({...form, weight: Number(e.target.value)})}/>
              <input type="number" step="0.1" placeholder="Length/Height (cm)" className="w-full px-3 py-2 rounded-xl border border-[#E5EBEB]" onChange={(e)=>setForm({...form, height: Number(e.target.value)})}/>
              <input type="number" step="0.1" placeholder="Head circumference (cm)" className="w-full px-3 py-2 rounded-xl border border-[#E5EBEB]" onChange={(e)=>setForm({...form, hc: Number(e.target.value)})}/>
            </>
          )}
          {tab === "vaccine" && (
            <>
              <input placeholder="Vaccine name" className="w-full px-3 py-2 rounded-xl border border-[#E5EBEB]" onChange={(e)=>setForm({...form, name: e.target.value})}/>
              <input type="date" required className="w-full px-3 py-2 rounded-xl border border-[#E5EBEB]" onChange={(e)=>setForm({...form, given_on: e.target.value})}/>
              <input placeholder="Notes / next due" className="w-full px-3 py-2 rounded-xl border border-[#E5EBEB]" onChange={(e)=>setForm({...form, notes: e.target.value})}/>
              <div className="text-xs text-[#5A6D6D] p-3 bg-[#F0F7F7] rounded-xl mt-2 space-y-1 max-h-40 overflow-y-auto">
                <div className="font-bold">Suggested schedule (IAP):</div>
                {VAX.map((v,i)=><div key={i}><b>{v.age}:</b> {v.vax}</div>)}
              </div>
            </>
          )}
          {tab === "symptom" && (
            <>
              <input type="date" required className="w-full px-3 py-2 rounded-xl border border-[#E5EBEB]" onChange={(e)=>setForm({...form, date: e.target.value})}/>
              <textarea required rows={4} placeholder="Describe symptoms..." className="w-full px-3 py-2 rounded-xl border border-[#E5EBEB]" onChange={(e)=>setForm({...form, notes: e.target.value})}/>
            </>
          )}
          <button className="btn-primary w-full justify-center" data-testid="tracker-save">Save entry</button>
        </form>

        <div className="card-soft">
          <h3 className="font-display font-bold mb-3">Recent entries</h3>
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {entries.length === 0 && <div className="text-sm text-[#5A6D6D]">No entries yet.</div>}
            {entries.map((e) => (
              <div key={e.id} className="p-3 bg-[#FAF9F6] rounded-xl border border-[#E5EBEB] text-sm">
                <div className="text-xs text-[#5A6D6D]">{new Date(e.date).toLocaleString()}</div>
                <div className="mt-1">{Object.entries(e.data).map(([k,v]) => <span key={k} className="mr-3"><b>{k}:</b> {String(v)}</span>)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
